import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type {
  AppUser,
  LoginRequest,
  LoginResponse,
  PinVerifyRequest,
  PinVerifyResponse,
} from 'shared';
import { SESSION_TIMEOUT_MINUTES, PIN_MAX_ATTEMPTS } from 'shared';
import { api, setAccessToken } from '@/lib/api';

/* ==========================================================================
   Auth Context — manages JWT, user state, lock timeout, PIN state.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

export interface AuthState {
  /** The currently authenticated user (null if logged out). */
  user: AppUser | null;
  /** Whether we're in the initial boot / token-check phase. */
  isLoading: boolean;
  /** True when user is authenticated (has valid token). */
  isAuthenticated: boolean;
  /** True when session is locked (requires PIN to unlock). */
  isLocked: boolean;
  /** Remaining PIN attempts before forced logout. */
  pinAttemptsRemaining: number;
}

export interface AuthActions {
  /** Log in with email + password. */
  login: (credentials: LoginRequest) => Promise<void>;
  /** Log out entirely (clears token + user). */
  logout: () => void;
  /** Verify PIN to unlock the session. */
  verifyPin: (pin: string) => Promise<boolean>;
  /** Manually lock the session. */
  lock: () => void;
}

export type AuthContextValue = AuthState & AuthActions;

/* --------------------------------------------------------------------------
   Context
   -------------------------------------------------------------------------- */

export const AuthContext = createContext<AuthContextValue | null>(null);

/* --------------------------------------------------------------------------
   Storage keys
   -------------------------------------------------------------------------- */

const TOKEN_KEY = 'bc_access_token';
const USER_KEY = 'bc_user';

/* --------------------------------------------------------------------------
   Provider
   -------------------------------------------------------------------------- */

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLocked, setIsLocked] = useState(false);
  const [pinAttempts, setPinAttempts] = useState(0);

  // Inactivity timer ref
  const lockTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // ------------------------------------------------------------------
  // Derived state
  // ------------------------------------------------------------------

  const isAuthenticated = user !== null;
  const pinAttemptsRemaining = PIN_MAX_ATTEMPTS - pinAttempts;

  // ------------------------------------------------------------------
  // Lock timer management
  // ------------------------------------------------------------------

  const resetLockTimer = useCallback(() => {
    if (lockTimerRef.current) clearTimeout(lockTimerRef.current);
    lockTimerRef.current = setTimeout(() => {
      setIsLocked(true);
    }, SESSION_TIMEOUT_MINUTES * 60 * 1000);
  }, []);

  const clearLockTimer = useCallback(() => {
    if (lockTimerRef.current) clearTimeout(lockTimerRef.current);
  }, []);

  // Listen for user interaction to reset timer
  useEffect(() => {
    if (!isAuthenticated || isLocked) return;

    const events = ['pointerdown', 'keydown', 'scroll'] as const;
    const handler = () => resetLockTimer();

    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    resetLockTimer();

    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      clearLockTimer();
    };
  }, [isAuthenticated, isLocked, resetLockTimer, clearLockTimer]);

  // ------------------------------------------------------------------
  // Boot: restore session from localStorage
  // ------------------------------------------------------------------

  useEffect(() => {
    try {
      const storedToken = localStorage.getItem(TOKEN_KEY);
      const storedUser = localStorage.getItem(USER_KEY);

      if (storedToken && storedUser) {
        const parsed = JSON.parse(storedUser) as AppUser;
        setAccessToken(storedToken);
        setUser(parsed);
        // Start locked — operator must PIN-in after refresh
        setIsLocked(true);
      }
    } catch {
      // Corrupt storage — clean up
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ------------------------------------------------------------------
  // Actions
  // ------------------------------------------------------------------

  const login = useCallback(async (credentials: LoginRequest) => {
    const data = await api.post<LoginResponse>('/auth/login', credentials);
    setAccessToken(data.accessToken);
    localStorage.setItem(TOKEN_KEY, data.accessToken);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setUser(data.user);
    setIsLocked(false);
    setPinAttempts(0);
  }, []);

  const logout = useCallback(() => {
    setAccessToken(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
    setIsLocked(false);
    setPinAttempts(0);
    clearLockTimer();
  }, [clearLockTimer]);

  const lock = useCallback(() => {
    setIsLocked(true);
    clearLockTimer();
  }, [clearLockTimer]);

  const verifyPin = useCallback(
    async (pin: string): Promise<boolean> => {
      try {
        const res = await api.post<PinVerifyResponse>(
          '/auth/pin/verify',
          { pin } satisfies PinVerifyRequest,
        );
        // Il backend rigenera l'access token al pin verify (quello vecchio puo'
        // essere scaduto). Senza questo riaggancio le successive chiamate API
        // partono con un JWT stale → 401 silenti → dashboard vuota.
        if (res?.accessToken) {
          setAccessToken(res.accessToken);
          localStorage.setItem(TOKEN_KEY, res.accessToken);
        }
        setIsLocked(false);
        setPinAttempts(0);
        resetLockTimer();
        return true;
      } catch {
        const next = pinAttempts + 1;
        setPinAttempts(next);
        if (next >= PIN_MAX_ATTEMPTS) {
          // Too many attempts → full logout
          logout();
        }
        return false;
      }
    },
    [pinAttempts, logout, resetLockTimer],
  );

  // ------------------------------------------------------------------
  // Memoised context value
  // ------------------------------------------------------------------

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated,
      isLocked,
      pinAttemptsRemaining,
      login,
      logout,
      verifyPin,
      lock,
    }),
    [
      user,
      isLoading,
      isAuthenticated,
      isLocked,
      pinAttemptsRemaining,
      login,
      logout,
      verifyPin,
      lock,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
